import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';

// app: components
import BookTile from 'components/BookTile';
import Button from 'components/Button';
import Link from 'components/Link';
import Price from 'components/Price';

// app: services
import { isApp } from 'services/environment';
import history from 'services/history';
import route from 'services/route';
import { openWindow } from 'services/inAppBrowser';
import { showHelpArticle, ARTICLES } from 'services/intercom';

// app: queries
import GetAlacarteBookOffer from './GetAlacarteBookOffer.gql';

// assets
import CssAlacarteOffer from './AlacarteOffer.scss';

function AlacarteOffer(props) {
  const {
    className,
    isbn,
    hideButton,
    purchasing,
    purchaseComplete,
    withAlacarteIndicator,
  } = props;
  useStyles(CssAlacarteOffer);
  const loggedIn = useSelector(state => state.user.logged_in);
  const country = useSelector(state => state.paywall.country || state.user.geolocation.countryCode);

  const isUniversalLink = useSelector(state => state.router.locationState.universalLink);
  const baseUrl = useSelector(state => state.config.baseUrl);
  const pathname = useSelector(state => state.router.pathname);

  // If the user arrives on a universal link to a screen that is selling a la carte then we shall
  // bounce them to the website where they can checkout.
  useEffect(
    () => {
      if (isApp() && isUniversalLink && !purchaseComplete) {
        const url = `${baseUrl}${pathname}`;
        openWindow(url, '_blank');
        // Clear the state so they don't get kicked out again upon return to the app
        history.replace({ state: null });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isUniversalLink],
  );

  const { data, previousData, loading } = useQuery(GetAlacarteBookOffer, {
    variables: {
      isbn,
      country,
    },
    fetchPolicy: isApp() ? 'cache-and-network' : 'cache-first',
    errorPolicy: 'all',
    ssr: false,
  });

  const book = data?.Book || previousData?.Book || {};
  const price = (
    <span className="alc-offer__price">
      {data?.Book?.price?.offer_price ? (
        <Price
          currency={book?.price?.currency}
          amount={book?.price?.offer_price}
          originalPrice={book?.price?.list_price}
          originalPriceClassName="alc-offer__original-price"
        />
      ) : (
        <Skeleton count={2} width={100} />
      )}
    </span>
  );
  let text;
  const priceAndDiscountText = (
    <React.Fragment>
      <p>{price}</p>
      {book?.price?.discount_text ? (
        <p className="alc-offer__offer-text">{book.price.discount_text}</p>
      ) : null}
    </React.Fragment>
  );
  if (book?.alacarte_available === false) {
    text = (
      <p>
        Sorry, this <em>à la carte</em> cookbook is not currently available to buy in your country
      </p>
    );
  } else if (purchasing) {
    const priceText = (
      <React.Fragment>
        {book.title ? (
          <p>
            Purchase <em>{book.title}</em> for a one-off payment of
          </p>
        ) : (
          <Skeleton count={2} width="100%" />
        )}
        {priceAndDiscountText}
      </React.Fragment>
    );
    text = purchaseComplete ? <p>Purchase complete</p> : priceText;
  } else {
    text = (
      <React.Fragment>
        <p>
          This <em>à la carte</em>{' '}
          {loggedIn
            ? 'cookbook can be added to your ckbk membership'
            : 'title is available to ckbk members'}{' '}
          for a one-off payment of
        </p>
        {priceAndDiscountText}
        {isApp() ? (
          <React.Fragment>
            <p className="margin-top-5">
              <strong>Visit app.ckbk.com to purchase </strong>
            </p>
            <p>
              <button
                className="link link--energized"
                onClick={() => showHelpArticle(ARTICLES.accessingAlacarte)}
              >
                more info
              </button>
            </p>
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  }
  const clickable = !(isApp() || hideButton || !book.alacarte_available);
  const linkUrl = route.alacartePurchase(isbn, book.signature_);
  const content = (
    <div
      className={classNames('alc-offer__box-inner', {
        'alc-offer__box-inner--clickable': clickable,
      })}
    >
      <div>
        <BookTile
          withAlacarteIndicator={withAlacarteIndicator}
          withDiscountText={false}
          width={160}
          height={160}
          linkUrl={clickable ? linkUrl : null}
          {...book}
        />
      </div>
      <div>{text}</div>
    </div>
  );

  return (
    <div
      className={classNames('alc-offer', className, {
        'alc-offer--clickable': clickable,
        'alc-offer--loading': loading,
      })}
    >
      {clickable ? <Link to={linkUrl}>{content}</Link> : content}

      {clickable ? (
        <Button href={linkUrl} size="extra-large" themeName="energized" className="margin-top-10">
          {loggedIn ? 'Add to my library' : 'Join ckbk to get started'}
        </Button>
      ) : null}
    </div>
  );
}

AlacarteOffer.propTypes = {
  className: PropTypes.string,
  hideButton: PropTypes.bool,
  isbn: PropTypes.string.isRequired,
  purchasing: PropTypes.bool,
  purchaseComplete: PropTypes.bool,
  withAlacarteIndicator: PropTypes.bool,
};

AlacarteOffer.defaultProps = {
  className: null,
  hideButton: false,
  purchasing: false,
  purchaseComplete: false,
  withAlacarteIndicator: false,
};

export default AlacarteOffer;
